/**
 * AssistantSettings.jsx
 * src/pages/assistant/AssistantSettings.jsx
 */

import { useState } from "react";
import { apiFetch } from "../../components/helpers";


function SectionCard({ title, subtitle, children }) {
  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
      <div className="card-header border-0 px-4 py-3" style={{ background: "#FAFBFC", borderRadius: "16px 16px 0 0" }}>
        <h6 className="fw-bold mb-1" style={{ color: "#0A1628", fontSize: 14 }}>{title}</h6>
        {subtitle && <p className="text-muted mb-0" style={{ fontSize: 12 }}>{subtitle}</p>}
      </div>
      <div className="card-body px-4 py-4">{children}</div>
    </div>
  );
}

function FeedbackMsg({ text, ok = true }) {
  if (!text) return null;
  return <div className={`alert alert-${ok ? "success" : "danger"} py-2 px-3 mb-3`} style={{ fontSize: 12 }}>{text}</div>;
}

function ToggleRow({ label, desc, checked, onChange }) {
  return (
    <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "#0A1628" }}>{label}</div>
        {desc && <div className="text-muted" style={{ fontSize: 11 }}>{desc}</div>}
      </div>
      <div className="form-check form-switch mb-0 ms-3">
        <input className="form-check-input" type="checkbox" checked={checked}
          onChange={e => onChange(e.target.checked)} style={{ cursor: "pointer", width: 42, height: 22 }} />
      </div>
    </div>
  );
}

function SaveBtn({ onClick, saving, label = "Save Changes" }) {
  return (
    <button className="btn fw-bold text-white" style={{ background: "#D85A30" }} onClick={onClick} disabled={saving}>
      {saving ? <><span className="spinner-border spinner-border-sm me-1" />{label}...</> : label}
    </button>
  );
}

export default function AssistantSettings() {

  /* ── Notifications ── */
  const [notifs, setNotifs] = useState({
    new_appointment:   true,
    appointment_change: true,
    patient_checkin:   true,
    reminder_due:      true,
    low_inventory:     true,
    messages:          true,
    daily_summary:     false,
    email_digest:      false,
  });
  const [notifMsg,    setNotifMsg]    = useState({ text: "", ok: true });
  const [notifSaving, setNotifSaving] = useState(false);

  const saveNotifs = async () => {
    setNotifSaving(true); setNotifMsg({ text: "", ok: true });
    try {
      const json = await apiFetch("update_notifications", "PUT", notifs);
      setNotifMsg({ text: json.status ? "Preferences saved!" : json.message || "Failed", ok: !!json.status });
    } catch { setNotifMsg({ text: "Network error", ok: false }); }
    setNotifSaving(false);
    setTimeout(() => setNotifMsg({ text: "", ok: true }), 4000);
  };

  /* ── Working Preferences ── */
  const [prefs, setPrefs] = useState({
    working_hours_start: "09:00",
    working_hours_end:   "18:00",
    default_checkin_status: "Waiting",
    auto_send_reminders: false,
    reminder_lead_time:  "24",
  });
  const [prefsMsg,    setPrefsMsg]    = useState({ text: "", ok: true });
  const [prefsSaving, setPrefsSaving] = useState(false);

  const savePrefs = async () => {
    setPrefsSaving(true); setPrefsMsg({ text: "", ok: true });
    try {
      const json = await apiFetch("update_assistant_prefs", "PUT", prefs);
      setPrefsMsg({ text: json.status ? "Preferences saved!" : json.message || "Failed", ok: !!json.status });
    } catch { setPrefsMsg({ text: "Network error", ok: false }); }
    setPrefsSaving(false);
    setTimeout(() => setPrefsMsg({ text: "", ok: true }), 4000);
  };

  const inp = "form-control";

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif"}}>
      <div className="mb-4">
        <h4 className="fw-bold mb-1" style={{ color: "#0A1628" }}>Settings</h4>
        <p className="text-muted mb-0" style={{ fontSize: 13 }}>Manage your assistant preferences and notifications</p>
      </div>

      {/* ── Notification Preferences ── */}
      <SectionCard title="Notification Preferences" subtitle="Choose which alerts you want to receive">
        <ToggleRow label="New appointment added"      desc="Notify when a new appointment is created"                  checked={notifs.new_appointment}   onChange={v => setNotifs({ ...notifs, new_appointment: v })} />
        <ToggleRow label="Appointment changed"        desc="Notify when an appointment is rescheduled or cancelled"    checked={notifs.appointment_change} onChange={v => setNotifs({ ...notifs, appointment_change: v })} />
        <ToggleRow label="Patient check-in alerts"    desc="Alert when a patient arrives at the clinic"                checked={notifs.patient_checkin}   onChange={v => setNotifs({ ...notifs, patient_checkin: v })} />
        <ToggleRow label="Reminder due"               desc="Alert when a follow-up or reminder is due"                 checked={notifs.reminder_due}      onChange={v => setNotifs({ ...notifs, reminder_due: v })} />
        <ToggleRow label="Low inventory alert"        desc="Notify when any item falls below minimum stock level"      checked={notifs.low_inventory}     onChange={v => setNotifs({ ...notifs, low_inventory: v })} />
        <ToggleRow label="New messages"               desc="Alert when you receive a new message"                      checked={notifs.messages}          onChange={v => setNotifs({ ...notifs, messages: v })} />
        <ToggleRow label="Daily summary"              desc="Receive a daily summary of appointments and tasks"         checked={notifs.daily_summary}     onChange={v => setNotifs({ ...notifs, daily_summary: v })} />
        <ToggleRow label="Email digest"               desc="Weekly email digest of activity"                           checked={notifs.email_digest}      onChange={v => setNotifs({ ...notifs, email_digest: v })} />
        <div className="mt-3">
          <FeedbackMsg text={notifMsg.text} ok={notifMsg.ok} />
          <SaveBtn onClick={saveNotifs} saving={notifSaving} label="Save Notifications" />
        </div>
      </SectionCard>

      {/* ── Working Preferences ── */}
      <SectionCard title="Working Preferences" subtitle="Set your default working hours and behaviour">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>WORKING HOURS START</label>
            <input type="time" className={inp} value={prefs.working_hours_start} onChange={e => setPrefs({ ...prefs, working_hours_start: e.target.value })} />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>WORKING HOURS END</label>
            <input type="time" className={inp} value={prefs.working_hours_end} onChange={e => setPrefs({ ...prefs, working_hours_end: e.target.value })} />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DEFAULT CHECK-IN STATUS</label>
            <select className="form-select" value={prefs.default_checkin_status} onChange={e => setPrefs({ ...prefs, default_checkin_status: e.target.value })}>
              {["Waiting","In Progress"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>REMINDER LEAD TIME (HOURS)</label>
            <input type="number" className={inp} value={prefs.reminder_lead_time} onChange={e => setPrefs({ ...prefs, reminder_lead_time: e.target.value })} placeholder="e.g. 24" />
            <div className="form-text">How many hours before appointment to send reminders</div>
          </div>
          <div className="col-12 pt-1">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="autoReminder" checked={prefs.auto_send_reminders}
                onChange={e => setPrefs({ ...prefs, auto_send_reminders: e.target.checked })} style={{ cursor: "pointer", width: 42, height: 22 }} />
              <label className="form-check-label ms-2" htmlFor="autoReminder" style={{ fontSize: 13 }}>
                Auto-send reminders before appointments
              </label>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <FeedbackMsg text={prefsMsg.text} ok={prefsMsg.ok} />
          <SaveBtn onClick={savePrefs} saving={prefsSaving} label="Save Preferences" />
        </div>
      </SectionCard>

      {/* ── Account Info (read-only) ── */}
      <SectionCard title="Account Information" subtitle="Your linked admin account details">
        <div className="p-3 rounded-3" style={{ background: "#F8FAFC", border: "1px solid #E4EAF0" }}>
          <div className="row g-2">
            {[
              ["Name",       JSON.parse(localStorage.getItem("user") || "{}").name  || "—"],
              ["Email",      JSON.parse(localStorage.getItem("user") || "{}").email || "—"],
              ["Role",       "Assistant"],
              ["Login Type", localStorage.getItem("logintype") || "—"],
            ].map(([k, v]) => (
              <div className="col-12 col-md-6" key={k}>
                <div className="d-flex gap-2">
                  <span className="text-muted fw-semibold" style={{ fontSize: 12, minWidth: 80 }}>{k}:</span>
                  <span style={{ fontSize: 13, color: "#0A1628" }}>{v}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            To change your email or account credentials, go to <strong>My Profile</strong>.
          </p>
        </div>
      </SectionCard>
    </div>
  );
}